// Phrases: the 8-counts of a map, and the loops made of them.
//
// A phrase runs from a beat that is count 1 to the next beat that is count 1. In a map from
// `deriveMap` after a shift that is not always 8 beats (the phrase where the fix was made is
// short or long); everywhere else it is. All times are seconds. Every function works on the
// beat lattice (grid.ts), so it also works before the first beat and after the last, and on
// uniform maps from `fitTaps`.

import { countOfBeat, beatIndexAt, timeOfBeatIndex, type CountMap } from './grid.js';
import { PHRASE_BEATS } from './patterns.js';

/** A phrase: [start, end) in seconds, where `start` is count 1 and `end` is the next count 1. */
export interface Phrase {
  readonly start: number;
  readonly end: number;
}

/** The number of phrases a loop may span. */
export type LoopPhrases = 1 | 2 | 4 | 8;

/** Largest lattice beat index I look at: a map with no count 1 anywhere cannot make this loop spin. */
const SEARCH_LIMIT = 1 << 20;

/** Greatest integer beat j <= i that is count 1. */
function startAtOrBefore(map: CountMap, i: number): number {
  let j = i;
  for (let k = 0; countOfBeat(map, j) !== 1; k++) {
    if (k > SEARCH_LIMIT) throw new RangeError('map has no count 1');
    j--;
  }
  return j;
}

/** Smallest integer beat j > i that is count 1. */
function startAfter(map: CountMap, i: number): number {
  let j = i + 1;
  for (let k = 0; countOfBeat(map, j) !== 1; k++) {
    if (k > SEARCH_LIMIT) throw new RangeError('map has no count 1');
    j++;
  }
  return j;
}

/** Beat index of the count 1 that starts the phrase containing t. */
function phraseStartBeat(map: CountMap, t: number): number {
  return startAtOrBefore(map, Math.floor(beatIndexAt(map, t)));
}

/** The 8-count containing t, as start (its count 1) and end (the next count 1). */
export function phraseAt(map: CountMap, t: number): Phrase {
  const j = phraseStartBeat(map, t);
  return { start: timeOfBeatIndex(map, j), end: timeOfBeatIndex(map, startAfter(map, j)) };
}

/** Do `phrases` whole 8-counts follow the count 1 at beat j: each runs 1 to 8 and is followed by the next 1? */
function wholeRun(map: CountMap, j: number, phrases: number): boolean {
  for (let k = 0; k < phrases; k++) {
    const at = j + k * PHRASE_BEATS;
    if (countOfBeat(map, at) !== 1 || startAfter(map, at) !== at + PHRASE_BEATS) return false;
  }
  return true;
}

/** The most count 1s `loopRegion` steps over looking for a whole run: it always ends, whatever the map. */
const LOOP_SEARCH = 1 << 14;

/**
 * A loop is `phrases` (1, 2, 4 or 8) whole 8-counts, starting at a count 1, so it always plays 1 to 8 and round again.
 * It starts at the count 1 of the phrase containing t. Where that phrase is short or long (a fix was made in it) or is followed
 * by one, the loop starts at the next count 1 that begins a whole run, so it never plays 1 2 3 and then 1 again.
 * Use it for "loop this 8-count" (`phrases` 1) and the x2, x4, x8 chips.
 */
export function loopRegion(map: CountMap, t: number, phrases: LoopPhrases): Phrase {
  if (phrases !== 1 && phrases !== 2 && phrases !== 4 && phrases !== 8) throw new RangeError('a loop is 1, 2, 4 or 8 phrases');
  const first = phraseStartBeat(map, t);
  let start = first;
  for (let k = 0; k < LOOP_SEARCH && !wholeRun(map, start, phrases); k++) start = startAfter(map, start);
  if (!wholeRun(map, start, phrases)) start = first; // no whole run found: the plain phrases from the playhead
  let end = start;
  for (let k = 0; k < phrases; k++) end = startAfter(map, end);
  return { start: timeOfBeatIndex(map, start), end: timeOfBeatIndex(map, end) };
}

/** The count 1 nearest to t: the start of its phrase, or of the next one, whichever is closer (a tie goes to the later). Loop handles snap to it. */
export function snapToPhrase(map: CountMap, t: number): number {
  const j = phraseStartBeat(map, t);
  const before = timeOfBeatIndex(map, j);
  const after = timeOfBeatIndex(map, startAfter(map, j));
  return t - before < after - t ? before : after;
}

/**
 * How many phrase starts lie after the phrase containing `from` up to and including the phrase containing t:
 * 0 in the same phrase, 1 in the next, negative before. Cost is the number of beats between them.
 */
export function phrasesBetween(map: CountMap, from: number, t: number): number {
  const a = phraseStartBeat(map, from);
  const b = phraseStartBeat(map, t);
  let count = 0;
  for (let i = a + 1; i <= b; i++) if (countOfBeat(map, i) === 1) count++;
  for (let i = b + 1; i <= a; i++) if (countOfBeat(map, i) === 1) count--;
  return count;
}
